import React from "react";
import { createPortal } from "react-dom";
import styled from "styled-components";
import {
	GroupedOrderDetailRow,
	GroupedStationRequestDetailItem,
	GroupedStationRequestDetailsResponse,
} from "../../../../services/PartnerPaymentRequestService";
import { formatPartnerPayoutMoney, ORDER_CURRENCY_CODE } from "./PartnerPayoutDetailsContent";

const ModalOverlay = styled.div`
	position: fixed;
	inset: 0;
	background-color: rgba(15, 23, 42, 0.58);
	backdrop-filter: blur(2px);
	display: flex;
	align-items: center;
	justify-content: center;
	z-index: 1000000;
	padding: 20px;
`;

const ModalContainer = styled.div`
	width: 1080px;
	max-width: 98vw;
	max-height: 90vh;
	background: #ffffff;
	border-radius: 16px;
	box-shadow: 0 20px 45px rgba(15, 23, 42, 0.26);
	display: flex;
	flex-direction: column;
	overflow: hidden;
`;

const Body = styled.div`
	padding: 18px 22px 24px;
	overflow-y: auto;
`;

const Head = styled.div`
	display: flex;
	align-items: flex-start;
	justify-content: space-between;
	gap: 12px;
	margin-bottom: 16px;
	border-bottom: 1px solid #e5e7eb;
	padding-bottom: 12px;
`;

const Title = styled.h3`
	margin: 0;
	font-size: 18px;
	font-weight: 700;
	color: #0f172a;
`;

const Sub = styled.p`
	margin: 6px 0 0;
	font-size: 13px;
	color: #64748b;
`;

const CloseButton = styled.button`
	border: none;
	background: transparent;
	color: #64748b;
	font-size: 24px;
	line-height: 1;
	cursor: pointer;
	padding: 4px;
	border-radius: 8px;
	flex-shrink: 0;
	transition: color 0.2s ease;
	&:hover {
		color: #0f172a;
	}
`;

/* ── Cycle summary strip ───────────────────────────────────────────── */
const CycleSummary = styled.div`
	display: flex;
	flex-wrap: wrap;
	align-items: stretch;
	gap: 10px;
	margin-bottom: 16px;
`;

const SummaryCard = styled.div`
	flex: 1 1 200px;
	padding: 12px 14px;
	border-radius: 12px;
	border: 1px solid #e2e8f0;
	background: #f8fafc;
`;

const SummaryLabel = styled.div`
	font-size: 11px;
	font-weight: 600;
	letter-spacing: 0.02em;
	text-transform: uppercase;
	color: #64748b;
`;

const SummaryValue = styled.div`
	margin-top: 4px;
	font-size: 18px;
	font-weight: 700;
	color: #0f172a;
	font-variant-numeric: tabular-nums;
`;

const CurrencyNote = styled.div`
	display: flex;
	align-items: center;
	gap: 6px;
	margin-bottom: 16px;
	font-size: 12px;
	color: #475569;
`;

/* ── Per-payout request card ───────────────────────────────────────── */
const RequestCard = styled.div`
	border: 1px solid #e2e8f0;
	border-radius: 14px;
	overflow: hidden;
	& + & {
		margin-top: 16px;
	}
`;

const CardHead = styled.div`
	display: flex;
	flex-wrap: wrap;
	align-items: center;
	gap: 10px;
	padding: 12px 16px;
	background: #f8fafc;
	border-bottom: 1px solid #e2e8f0;
`;

const PayoutId = styled.span`
	font-size: 14px;
	font-weight: 700;
	color: #0f172a;
`;

const Badge = styled.span<{ $bg: string; $text: string; $border: string }>`
	display: inline-flex;
	align-items: center;
	padding: 2px 9px;
	border-radius: 999px;
	font-size: 11px;
	font-weight: 600;
	line-height: 1.6;
	background: ${(p) => p.$bg};
	color: ${(p) => p.$text};
	border: 1px solid ${(p) => p.$border};
`;

/* ── Payout formula (Basis × rate = total) ─────────────────────────── */
const Formula = styled.div`
	display: flex;
	flex-wrap: wrap;
	align-items: flex-end;
	gap: 14px 18px;
	padding: 14px 16px;
	background: #ffffff;
`;

const Term = styled.div`
	display: flex;
	flex-direction: column;
	gap: 3px;
`;

const TermLabel = styled.span`
	font-size: 11px;
	font-weight: 600;
	letter-spacing: 0.02em;
	text-transform: uppercase;
	color: #64748b;
`;

const TermValue = styled.span<{ $strong?: boolean }>`
	font-size: ${(p) => (p.$strong ? "18px" : "15px")};
	font-weight: ${(p) => (p.$strong ? 700 : 600)};
	color: ${(p) => (p.$strong ? "#0f172a" : "#334155")};
	font-variant-numeric: tabular-nums;
	white-space: nowrap;
	& small {
		font-size: 11px;
		font-weight: 500;
		color: #94a3b8;
	}
`;

const Op = styled.span`
	font-size: 18px;
	font-weight: 600;
	color: #cbd5e1;
	padding-bottom: 2px;
`;

const FallbackTotal = styled.div`
	padding: 14px 16px;
	font-size: 14px;
	font-weight: 600;
	color: #0f172a;
	font-variant-numeric: tabular-nums;
	& span {
		margin-left: 8px;
		font-size: 12px;
		font-weight: 400;
		color: #94a3b8;
	}
`;

/* ── Booking sub-table ─────────────────────────────────────────────── */
const TableWrap = styled.div`
	overflow-x: auto;
	border-top: 1px solid #e2e8f0;
`;

const Table = styled.table`
	width: 100%;
	border-collapse: collapse;
	font-size: 12.5px;
	min-width: 680px;
`;

const Th = styled.th<{ $right?: boolean }>`
	text-align: ${(p) => (p.$right ? "right" : "left")};
	padding: 9px 14px;
	background: #ffffff;
	border-bottom: 1px solid #e2e8f0;
	color: #64748b;
	font-weight: 600;
	white-space: nowrap;
`;

const Tr = styled.tr`
	transition: background 0.15s ease;
	&:hover {
		background: #f8fafc;
	}
`;

const Td = styled.td<{ $right?: boolean; $muted?: boolean }>`
	padding: 9px 14px;
	border-bottom: 1px solid #f1f5f9;
	text-align: ${(p) => (p.$right ? "right" : "left")};
	color: ${(p) => (p.$muted ? "#94a3b8" : "#334155")};
	font-variant-numeric: tabular-nums;
	white-space: nowrap;
`;

const EmptyState = styled.div`
	padding: 32px;
	text-align: center;
	color: #64748b;
	border: 1px dashed #e2e8f0;
	border-radius: 12px;
`;

/* ── Helpers ───────────────────────────────────────────────────────── */

/** Basis = bag-days (quantity × days), NOT money — plain number, no currency, no forced trailing zeros. */
function formatUnits(n: number | null | undefined) {
	const v = Number(n);
	if (!Number.isFinite(v)) return "—";
	return v.toLocaleString("en-US", { maximumFractionDigits: 2 });
}

function formatIso(iso: string | null | undefined) {
	if (!iso) return "—";
	const d = new Date(iso);
	if (Number.isNaN(d.getTime())) return "—";
	return d.toLocaleString("en-GB", {
		day: "2-digit",
		month: "short",
		year: "numeric",
		hour: "2-digit",
		minute: "2-digit",
		hour12: false,
	});
}

/** OrderRefundStatus enum: 1 Idle, 2 Begin, 3 Done, 4 Terminated. Idle = no refund. */
function anyRealRefund(orders: GroupedOrderDetailRow[]): boolean {
	return orders.some((o) => {
		const n = Number(o.refundStatus);
		return Number.isFinite(n) && n !== 0 && n !== 1;
	});
}

function refundLabel(status: number | null | undefined): string {
	switch (Number(status)) {
		case 2:
			return "Refunding";
		case 3:
			return "Refunded";
		case 4:
			return "Refund cancelled";
		default:
			return "—";
	}
}

function requestTypeBadge(
	type: GroupedStationRequestDetailItem["requestType"]
): { label: string; bg: string; text: string; border: string } | null {
	if (type === "UNDER_MIN_PAYOUT") {
		return {
			label: "Below min payout",
			bg: "#FFFBEB",
			text: "#92400E",
			border: "#FDE68A",
		};
	}
	if (type === "FROM_MIN_PAYOUT") {
		return {
			label: "Above min payout",
			bg: "#ECFDF5",
			text: "#065F46",
			border: "#A7F3D0",
		};
	}
	return null;
}

function statusBadge(status: string | null | undefined): { label: string; bg: string; text: string; border: string } {
	const s = String(status || "").toUpperCase();
	if (s === "PAID") {
		return { label: "Paid", bg: "#ECFDF3", text: "#067647", border: "#A6F4C5" };
	}
	if (s === "IN_TRANSIT") {
		return { label: "In transit", bg: "#FFFAEB", text: "#B54708", border: "#FEDF89" };
	}
	if (s === "PENDING" || s === "REQUESTED") {
		return { label: "Requested", bg: "#EFF6FF", text: "#1D4ED8", border: "#BFDBFE" };
	}
	if (s === "REJECTED") {
		return { label: "Rejected", bg: "#FEF2F2", text: "#B91C1C", border: "#FECACA" };
	}
	return { label: s || "—", bg: "#F3F4F6", text: "#374151", border: "#E5E7EB" };
}

interface NormalizedGroup {
	id: number;
	requestAmount: number;
	requestType: GroupedStationRequestDetailItem["requestType"];
	status: string;
	com: number | null;
	basis: number | null;
	ccy: string | null;
	orders: GroupedOrderDetailRow[];
}

interface CycleBookingsBreakdownModalProps {
	isOpen: boolean;
	onClose: () => void;
	loading?: boolean;
	error?: string | null;
	payload: GroupedStationRequestDetailsResponse | null;
}

export default function CycleBookingsBreakdownModal({
	isOpen,
	onClose,
	loading = false,
	error = null,
	payload,
}: CycleBookingsBreakdownModalProps) {
	const payoutCcy = payload?.currencyCode ?? null;
	const ded = payload?.availableDeductionAmount;

	const t = {
		title: "Bookings in this cycle",
		totalPayout: "Total payout this cycle",
		deduction: "Ledger deduction",
		note: `Customer pays in ${ORDER_CURRENCY_CODE} · Station payout in ${payoutCcy || "local currency"}`,
		payout: "Payout",
		fBasis: "Volume",
		fRate: "Payout rate",
		fTotal: "Total payout",
		unitBagDays: "bag-days",
		perBagDay: "bag-day",
		noBreakdown: "(no calculation snapshot)",
		colOrder: "Order",
		colCustomer: "Customer",
		colQty: "Bags",
		colPaid: "Customer paid",
		colIn: "Check-in",
		colOut: "Check-out",
		colRefund: "Refund",
		empty: "No orders in this cycle.",
		loading: "Loading…",
	};

	// Build per-request groups from items (richest source); fall back to bookingLines.
	const items = payload?.items || [];
	const bookingLines = payload?.bookingLines || [];

	const ordersForItem = (it: GroupedStationRequestDetailItem): GroupedOrderDetailRow[] => {
		if (it.orders && it.orders.length) return it.orders;
		return bookingLines
			.filter((bl) => bl.paymentRequestId === it.id)
			.map((bl) => ({
				id: bl.orderId,
				status: bl.orderStatus,
				refundStatus: bl.refundStatus,
				customerName: bl.customerName,
				quantity: bl.quantity,
				totalMoney: bl.totalMoney,
				timeIn: bl.timeIn,
				timeOut: bl.timeOut,
			}));
	};

	let groups: NormalizedGroup[] = items.map((it) => ({
		id: it.id,
		requestAmount: it.requestAmount,
		requestType: it.requestType ?? null,
		status: it.status,
		com: it.effectiveStationCom,
		basis: it.payoutBasisUnits,
		ccy: it.pricingCurrencyCode || payoutCcy,
		orders: ordersForItem(it),
	}));

	// Fallback: no items but denormalized lines exist → group by request id.
	if (!groups.length && bookingLines.length) {
		const byReq = new Map<number, NormalizedGroup>();
		bookingLines.forEach((bl) => {
			let g = byReq.get(bl.paymentRequestId);
			if (!g) {
				g = {
					id: bl.paymentRequestId,
					requestAmount: bl.requestAmount,
					requestType: null,
					status: "",
					com: bl.effectiveStationCom,
					basis: bl.payoutBasisUnits,
					ccy: payoutCcy,
					orders: [],
				};
				byReq.set(bl.paymentRequestId, g);
			}
			g.orders.push({
				id: bl.orderId,
				status: bl.orderStatus,
				refundStatus: bl.refundStatus,
				customerName: bl.customerName,
				quantity: bl.quantity,
				totalMoney: bl.totalMoney,
				timeIn: bl.timeIn,
				timeOut: bl.timeOut,
			});
		});
		groups = Array.from(byReq.values());
	}

	const cycleTotal = groups.reduce((sum, g) => sum + (Number(g.requestAmount) || 0), 0);

	if (!isOpen) return null;

	const modalContent = (
		<ModalOverlay onClick={onClose}>
			<ModalContainer onClick={(e) => e.stopPropagation()}>
				<Body>
					<Head>
						<div>
							<Title>{t.title}</Title>
							<Sub>
								{payload?.stationName ?? "—"}
								{payload?.cycleLabel ? ` • ${payload.cycleLabel}` : ""}
							</Sub>
						</div>
						<CloseButton
							type="button"
							onClick={onClose}
							aria-label="Close">
							×
						</CloseButton>
					</Head>

					{loading ? (
						<div style={{ padding: "24px", color: "#64748b" }}>{t.loading}</div>
					) : error ? (
						<div style={{ padding: "24px", color: "#b91c1c" }}>{error}</div>
					) : groups.length === 0 ? (
						<EmptyState>{t.empty}</EmptyState>
					) : (
						<>
							<CycleSummary>
								<SummaryCard>
									<SummaryLabel>{t.totalPayout}</SummaryLabel>
									<SummaryValue>{formatPartnerPayoutMoney(cycleTotal, payoutCcy)}</SummaryValue>
								</SummaryCard>
								{ded != null && Number.isFinite(Number(ded)) ? (
									<SummaryCard>
										<SummaryLabel>{t.deduction}</SummaryLabel>
										<SummaryValue>{formatPartnerPayoutMoney(ded, payoutCcy)}</SummaryValue>
									</SummaryCard>
								) : null}
							</CycleSummary>

							<CurrencyNote>
								<span aria-hidden>ⓘ</span>
								{t.note}
							</CurrencyNote>

							{groups.map((g) => {
								const typeBadge = requestTypeBadge(g.requestType);
								const stBadge = g.status ? statusBadge(g.status) : null;
								const hasFormula = g.basis != null && g.com != null;
								const showRefund = anyRealRefund(g.orders);
								return (
									<RequestCard key={g.id}>
										<CardHead>
											<PayoutId>
												{t.payout} #{g.id}
											</PayoutId>
											{typeBadge ? (
												<Badge
													$bg={typeBadge.bg}
													$text={typeBadge.text}
													$border={typeBadge.border}>
													{typeBadge.label}
												</Badge>
											) : null}
											{stBadge ? (
												<Badge
													$bg={stBadge.bg}
													$text={stBadge.text}
													$border={stBadge.border}>
													{stBadge.label}
												</Badge>
											) : null}
										</CardHead>

										{hasFormula ? (
											<Formula>
												<Term>
													<TermLabel>{t.fBasis}</TermLabel>
													<TermValue>
														{formatUnits(g.basis)} <small>{t.unitBagDays}</small>
													</TermValue>
												</Term>
												<Op aria-hidden>×</Op>
												<Term>
													<TermLabel>{t.fRate}</TermLabel>
													<TermValue>
														{formatPartnerPayoutMoney(g.com, g.ccy)}{" "}
														<small>/{t.perBagDay}</small>
													</TermValue>
												</Term>
												<Op aria-hidden>=</Op>
												<Term>
													<TermLabel>{t.fTotal}</TermLabel>
													<TermValue $strong>
														{formatPartnerPayoutMoney(g.requestAmount, g.ccy)}
													</TermValue>
												</Term>
											</Formula>
										) : (
											<FallbackTotal>
												{t.fTotal}: {formatPartnerPayoutMoney(g.requestAmount, g.ccy)}
												<span>{t.noBreakdown}</span>
											</FallbackTotal>
										)}

										<TableWrap>
											<Table>
												<thead>
													<tr>
														<Th>{t.colOrder}</Th>
														<Th>{t.colCustomer}</Th>
														<Th $right>{t.colQty}</Th>
														<Th $right>{t.colPaid}</Th>
														<Th>{t.colIn}</Th>
														<Th>{t.colOut}</Th>
														{showRefund ? <Th>{t.colRefund}</Th> : null}
													</tr>
												</thead>
												<tbody>
													{g.orders.map((o) => (
														<Tr key={o.id}>
															<Td>#{o.id}</Td>
															<Td>{o.customerName || "—"}</Td>
															<Td $right>{o.quantity ?? "—"}</Td>
															<Td $right>
																{formatPartnerPayoutMoney(
																	o.totalMoney,
																	ORDER_CURRENCY_CODE
																)}
															</Td>
															<Td>{formatIso(o.timeIn)}</Td>
															<Td>{formatIso(o.timeOut)}</Td>
															{showRefund ? <Td>{refundLabel(o.refundStatus)}</Td> : null}
														</Tr>
													))}
												</tbody>
											</Table>
										</TableWrap>
									</RequestCard>
								);
							})}
						</>
					)}
				</Body>
			</ModalContainer>
		</ModalOverlay>
	);

	return createPortal(modalContent, document.body);
}
